import { computed, ref } from 'vue'
import { ElMessage } from 'element-plus'
import useAppStore from '@/store/modules/app'
import { formatMinutesTooltip, formatSecondsTooltip } from './composables/constants'
import { useIntervals } from './composables/useIntervals'
import { useReceivers } from './composables/useReceivers'
import { useStationAlarms } from './composables/useStationAlarms'
import { useStationAlarmCodes } from './composables/useStationAlarmCodes'
import type { StationRow } from './composables/types'

// 类型对外再导出，保持 `import type { CatKey } from '.../useAlarmConfig'` 形式仍可用
export type {
  CatKey,
  AlarmCategory,
  AlarmTypeDef,
  PushCfg,
  PollCfg,
  ReceiverGroup,
  StationRow,
  DeviceGroupKey,
  DeviceGroup,
  AlarmCodeDef,
} from './composables/types'

/**
 * 告警配置共享状态与方法（模块级单例）。
 *
 * 父容器 AlarmConfig/index.vue 与各 panel 子组件通过本函数共享同一份实例：
 * 首次调用时创建，后续调用直接复用，因此任一 panel 的修改都会被
 * 「保存全部配置」正确读取。状态按职责拆分到 composables/ 下：
 *   - useIntervals          推送/轮询间隔
 *   - useReceivers          推送接收人分组
 *   - useStationAlarms      电站告警开关（运营）
 *   - useStationAlarmCodes  电站告警码（设备）
 */
let instance: ReturnType<typeof createAlarmConfig> | null = null

export function useAlarmConfig() {
  if (!instance) instance = createAlarmConfig()
  return instance
}

function createAlarmConfig() {
  const activeTab = ref<'push' | 'poll' | 'receiver' | 'station-alarm-code' | 'station'>('push')

  const appStore = useAppStore()
  const stations = computed<StationRow[]>(() =>
    appStore.stations.map((s) => ({ id: s.stationId.toString(), name: s.name })),
  )

  // stations 为各子模块共享依赖，统一注入以保证单例语义
  const intervals = useIntervals()
  const receivers = useReceivers(stations)
  const stationAlarms = useStationAlarms(stations)
  const stationAlarmCodes = useStationAlarmCodes(stations)

  function saveAllConfig() {
    // 先把正在编辑的视图写回各自的数据源
    stationAlarms.flushSelected()
    stationAlarmCodes.applyCurCodesToSelected()
    const payload = {
      pushIntervals: intervals.pushIntervals.value.map((p) => ({ code: p.code, intervalMinutes: p.interval })),
      pollIntervals: intervals.pollIntervals.value.map((p) => ({ job: p.job, intervalSeconds: p.interval })),
      groups: receivers.groups.value.map((g) => ({
        id: g.id,
        name: g.name,
        members: [...g.members],
        stations: [...g.stations],
        channels: { ...g.channels },
      })),
      stationAlarms: Object.entries(stationAlarms.stationAlarmsMap).map(([sid, alarms]) => ({
        stationId: sid,
        alarms,
      })),
      stationAlarmCodes: Object.entries(stationAlarmCodes.stationCodesMap).map(([sid, codes]) => ({
        stationId: sid,
        codes,
      })),
    }
    console.log('保存全部配置:', JSON.stringify(payload, null, 2))
    ElMessage.success('全部配置已保存')
  }

  function resetAll() {
    intervals.pushIntervals.value.forEach((p) => {
      p.interval = p.defaultVal
    })
    intervals.pollIntervals.value.forEach((p) => {
      p.interval = p.defaultVal
    })
    ElMessage.info('已恢复默认值（未保存）')
  }

  // 模块加载时初始化选中第一个电站，与原组件行为一致
  const first = stations.value[0]
  if (first) {
    stationAlarms.selectStation(first)
    stationAlarmCodes.selectedCodeStationIds.value = [first.id]
    stationAlarmCodes.recomputeCurCodes()
  }

  return {
    // 通用
    activeTab,
    stations,
    formatMinutesTooltip,
    formatSecondsTooltip,
    // 推送/轮询间隔
    ...intervals,
    // 推送接收人
    ...receivers,
    // 电站告警开关（运营）
    ...stationAlarms,
    // 电站告警码（设备）
    ...stationAlarmCodes,
    // 底部操作
    saveAllConfig,
    resetAll,
  }
}
